// 编辑项目设置，同时保留选中的 DaVinci 项目映射。
import { zodResolver } from '@hookform/resolvers/zod'
import { X } from 'lucide-react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  Separator,
  type SelectOption,
} from '../../base'
import { DaVinciProjectPicker } from '../davinci-project-picker'
import { NasPathPicker } from '../nas-path-picker'
import { NasStoragePicker } from '../nas-storage-picker'
import { ProjectCombobox } from './project-combobox'
import type {
  ProjectDrawerExternalProject,
  ProjectDrawerNasStorage,
  ProjectDrawerParams,
  ProjectDrawerTemplate,
  ProjectFormValues,
} from './type'

const fieldClassName =
  'h-11 focus-visible:border-brand-primary focus-visible:bg-surface-elevated focus-visible:ring-1 focus-visible:ring-focus-ring'

const useProjectSchema = (mode: ProjectDrawerParams['mode']) => {
  const { t } = useTranslation()

  return useMemo(
    () => {
      const required = t('This field is required')
      return z.object({
        davinciProjectName: z
          .string()
          .max(255),
        davinciProjectPath: z
          .string()
          .max(1024),
        description: z
          .string()
          .trim()
          .max(100),
        externalId:
          mode === 'create'
            ? z
                .string()
                .min(1, required)
            : z.string(),
        externalIdType: z.enum(['work_item_id']),
        externalType: z.enum(['feishu-project']),
        localNasStorageId:
          mode === 'create'
            ? z
                .string()
                .min(1, required)
            : z.string(),
        localNasStorageKey: z
          .string()
          .trim()
          .min(1, required),
        name: z
          .string()
          .trim()
          .min(1, required)
          .max(255),
        templateId:
          mode === 'create'
            ? z
                .string()
                .min(1, required)
            : z.string(),
      })
    },
    [mode, t],
  )
}

type ProjectPanelProps = {
  externalProjects: ProjectDrawerExternalProject[]
  isExternalProjectsLoading: boolean
  isNasStoragesLoading: boolean
  isTemplatesLoading: boolean
  nasStorages: ProjectDrawerNasStorage[]
  onClose: () => void
  onConfirm: (values: ProjectFormValues) => void
  onProjectSearchChange: (value: string) => void
  params: ProjectDrawerParams
  templates: ProjectDrawerTemplate[]
}

export const ProjectPanel = ({
  externalProjects,
  isExternalProjectsLoading,
  isNasStoragesLoading,
  isTemplatesLoading,
  nasStorages,
  onClose,
  onConfirm,
  onProjectSearchChange,
  params,
  templates,
}: ProjectPanelProps) => {
  const { t } = useTranslation()
  const isCreating = params.mode === 'create'
  const schema = useProjectSchema(params.mode)
  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildDefaultValues(params),
  })
  const projectName = form.watch('name')
  const davinciProjectPath = form.watch('davinciProjectPath')
  const localNasStorageId = form.watch('localNasStorageId')

  const templateOptions = useMemo<SelectOption[]>(
    () => templates.map(template => ({ label: template.name, value: template.id })),
    [templates],
  )
  const nasStorageOptions = useMemo<SelectOption[]>(
    () => nasStorages.map(storage => ({ label: storage.name, value: storage.id })),
    [nasStorages],
  )

  const selectExternalProject = (externalId: string) => {
    const project = externalProjects.find(item => item.externalId === externalId)
    form.setValue('externalId', externalId, { shouldDirty: true, shouldValidate: true })
    form.setValue('externalIdType', project?.externalIdType ?? 'work_item_id')
    form.setValue('externalType', project?.externalType ?? 'feishu-project')
    form.setValue('name', project?.name ?? '', { shouldDirty: true, shouldValidate: true })
    form.setValue('davinciProjectName', project?.name ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const submit = (values: ProjectFormValues) => {
    onConfirm({
      ...values,
      description: values.description.trim(),
      localNasStorageKey: values.localNasStorageKey.trim(),
      name: values.name.trim(),
    })
  }

  return (
    <Form {...form}>
      <form
        className="box-border flex h-full w-full flex-col rounded-l-panel border-l border-border-subtle bg-surface-panel px-6 py-3"
        onSubmit={form.handleSubmit(submit)}
      >
        <div className="flex w-full shrink-0 items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-base font-semibold leading-6 text-text-primary">
              {t(isCreating ? 'New project' : 'Edit project')}
            </span>
            <span className="text-xs leading-[18px] text-text-secondary">
              {t(
                isCreating
                  ? 'Create a project from an external source and its primary deliverable.'
                  : 'Update project information and its DaVinci project mapping.',
              )}
            </span>
          </div>

          <Button aria-label={t('Close panel')} onClick={onClose} size="icon-sm" type="button" variant="ghost">
            <X className="size-5" />
          </Button>
        </div>

        <Separator className="mt-1 mb-5" />

        <div className="-mx-2 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-2 py-1">
          <FormField
            control={form.control}
            name="externalId"
            render={({ field }) => (
              <FormItem>
                <FormLabel required={isCreating}>{t('Project')}</FormLabel>
                <ProjectCombobox
                  ariaLabel={t('Project')}
                  disabled={!isCreating}
                  fallbackLabel={isCreating ? undefined : params.project.name}
                  loading={isExternalProjectsLoading}
                  onSearchChange={onProjectSearchChange}
                  onValueChange={selectExternalProject}
                  options={externalProjects}
                  value={field.value}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('Project name')}</FormLabel>
                <FormControl>
                  <Input
                    aria-label={t('Project name')}
                    className={fieldClassName}
                    maxLength={255}
                    placeholder={t('Enter text...')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="davinciProjectName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('DaVinci project name')}</FormLabel>
                <FormControl>
                  {isCreating ? (
                    <DaVinciProjectPicker
                      className="h-11 w-full"
                      disabled={false}
                      projectName={projectName}
                      projectPath={davinciProjectPath}
                      value={field.value}
                      onConfirm={async ({ davinciProjectName, davinciProjectPath }) => {
                        form.setValue('davinciProjectName', davinciProjectName, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                        form.setValue('davinciProjectPath', davinciProjectPath, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }}
                    />
                  ) : (
                    <Input
                      aria-label={t('DaVinci project name')}
                      className={fieldClassName}
                      maxLength={255}
                      placeholder={t('Enter text...')}
                      {...field}
                    />
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('Project description')}</FormLabel>
                <FormControl>
                  <Input
                    aria-label={t('Project description')}
                    className={fieldClassName}
                    maxLength={100}
                    placeholder={t('Enter text...')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="templateId"
            render={({ field }) => (
              <FormItem>
                <FormLabel required={isCreating}>{t('Render config template')}</FormLabel>
                <Select
                  ariaLabel={t('Render config template')}
                  disabled={!isCreating || isTemplatesLoading}
                  onValueChange={field.onChange}
                  options={templateOptions}
                  placeholder={isCreating ? t(isTemplatesLoading ? 'Loading' : 'Select a template') : '—'}
                  triggerClassName="h-11 w-full border-input"
                  value={field.value}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {isCreating && (
            <FormField
              control={form.control}
              name="localNasStorageId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t('NAS')}</FormLabel>
                  <NasStoragePicker
                    ariaLabel={t('Select NAS storage for {{name}}', { name: projectName })}
                    disabled={isNasStoragesLoading || nasStorageOptions.length === 0}
                    onValueChange={value => {
                      if (value !== field.value) {
                        field.onChange(value)
                        form.setValue('localNasStorageKey', '', {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    }}
                    options={nasStorageOptions}
                    placeholder={t(isNasStoragesLoading ? 'Loading' : 'Select a NAS storage')}
                    triggerClassName="h-11 w-full"
                    value={field.value || undefined}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="localNasStorageKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('Local NAS path')}</FormLabel>
                <FormControl>
                  {isCreating ? (
                    <NasPathPicker
                      className="h-11 w-full"
                      disabled={!localNasStorageId}
                      projectId={null}
                      projectName={projectName}
                      storageId={localNasStorageId || null}
                      value={field.value}
                      onConfirm={async localNasStorageKey => {
                        form.setValue('localNasStorageKey', localNasStorageKey, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }}
                    />
                  ) : (
                    <Input
                      aria-label={t('Local NAS path')}
                      className={fieldClassName}
                      placeholder={t('Enter text...')}
                      {...field}
                    />
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator className="mt-5 mb-2.5" />

        <div className="flex w-full shrink-0 justify-end">
          <Button
            className="h-auto rounded-md px-7.5 py-2.5 text-sm font-semibold"
            disabled={isCreating && (isExternalProjectsLoading || isNasStoragesLoading || isTemplatesLoading)}
            type="submit"
          >
            {t(isCreating ? 'Create' : 'Save')}
          </Button>
        </div>
      </form>
    </Form>
  )
}

const buildDefaultValues = (params: ProjectDrawerParams): ProjectFormValues => {
  if (params.mode === 'create') {
    return {
      davinciProjectName: '',
      davinciProjectPath: '',
      description: '',
      externalId: '',
      externalIdType: 'work_item_id',
      externalType: 'feishu-project',
      localNasStorageId: '',
      localNasStorageKey: '',
      name: '',
      templateId: '',
    }
  }

  return {
    davinciProjectName: params.project.davinciProjectName ?? '',
    davinciProjectPath: '',
    description: params.project.description ?? '',
    externalId: params.project.externalId ?? '',
    externalIdType: 'work_item_id',
    externalType: 'feishu-project',
    localNasStorageId: '',
    localNasStorageKey: params.project.localNasStorageKey ?? '',
    name: params.project.name,
    templateId: '',
  }
}
